import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaSearch } from "react-icons/fa";
import { TAllMarkets } from "../../../types/markets/market";
import { useDispatch } from "react-redux";
import { setSelectedMarketId } from "../../../reducers/marketSlice";
import { Input } from "../../../components/ui";

type MarketSelectorProps = {
  markets: TAllMarkets[];
  selectedMarket: TAllMarkets | null;
  onChange: (market: TAllMarkets) => void;
  placeholder?: string;
};

export default function MarketSelectorDropdown({
  markets,
  selectedMarket,
  onChange,
  placeholder = "Select market...",
}: MarketSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    window.addEventListener("click", handleClickOutside, true);
    return () => window.removeEventListener("click", handleClickOutside, true);
  }, []);

  // Filter markets by search
  const filteredMarkets = markets.filter((m) =>
    m.marketName.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (market: TAllMarkets) => {
    onChange(market);
    setOpen(false);
    setSearch("");
    dispatch(setSelectedMarketId(market.marketId));
  };

  return (
    <div ref={dropdownRef} className="relative w-25">
      <div
        className="  flex items-center cursor-pointer justify-between bg-white"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex flex-col flex-1">
          <span className="text-sm">
            {selectedMarket ? selectedMarket.marketName : placeholder}
          </span>
          <span className="text-xs color-sub-text">
            MARKET ID:{" "}
            <span className="color-blue">{selectedMarket?.marketId}</span>
          </span>
          <span className="text-xs color-sub-text">
            {[
              selectedMarket?.addressLine,
              selectedMarket?.city,
              selectedMarket?.state,
              selectedMarket?.pincode,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
        </div>
        <FaChevronDown
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          size={14}
        />
      </div>

      {open && (
        <div className="absolute z-[999] w-full bg-white border rounded-md mt-1 shadow-md flex flex-col gap-2  max-h-80 overflow-auto p-2">
          <div className="relative">
            <FaSearch
              className=" absolute color-primary"
              size={15}
              style={{ top: "14px", left: "6px" }}
            />
            <Input
              type="text"
              palceholder="Search Market Name"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              inputStyle={{
                textIndent: "14px",
                padding: "8px 5px",
              }}
              style={{
                width: "100%",
              }}
            />
          </div>
          {filteredMarkets.length > 0 ? (
            filteredMarkets.map((market) => (
              <div
                key={market.marketId}
                className={`px-3 py-2 cursor-pointer border br-10 hover:bg-light-primary ${
                  selectedMarket?.marketId === market.marketId
                    ? "bg-light-primary border-primary"
                    : ""
                }`}
                onClick={() => handleSelect(market)}
              >
                <div className="flex flex-col">
                  <span className="text-sm">{market.marketName}</span>
                  <span className="text-xs color-sub-text">
                    MARKET ID:{" "}
                    <span className="color-blue">{market?.marketId}</span>
                  </span>
                  <span className="text-xs color-sub-text">
                    {[
                      market.addressLine,
                      market.city,
                      market.state,
                      market.pincode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">
              No markets found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
